/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { IButtonProps } from '@fluentui/react/lib/components/Button/Button.types';
import * as React from 'react';
import { useCallback } from 'react';
import { ApprovalType } from '../../../service/gen/esign';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { FileAttachments } from '../../../views/components';
import { CreateEsignApprovalComponentProps } from '../../../views/Esign/CreateEsignApproval/CreateEsignApprovalComponentProps';
import { RecipientList } from '../../../views/Esign/RecipientList';
import { InputState } from '../../../views/Esign/utils/forms';
import { MobileTextField, TextFieldType } from '../../components/MobileTextField/MobileTextField';
import { EsignNavigationBar } from '../EsignNavigationBar/EsignNavigationBar';
import { RecipientListInput } from '../RecipientList/RecipientListInput';

export const CreateEsignApprovalMobileComponent: React.FC<CreateEsignApprovalComponentProps> = (
    props: CreateEsignApprovalComponentProps
): JSX.Element => {
    const { form, fileService, fileUploadAcceptedTypes, signApprovalType, onBack, onNext } = props;
    const [showRecipientListView, setShowRecipientListView] = React.useState(false);
    const host = getAppHost();
    const loc = host.getLocale();

    const updateListenerCallback = useCallback(
        (input: InputState<string>, errorMessage: string, newValue: string): void => {
            input.setValue(errorMessage ? '' : newValue);
            input.setErrorMessage(errorMessage);
        },
        []
    );

    const formComponent = (
        <>
            {/* Title */}
            <MobileTextField
                title={loc.strings.request_title_text}
                updateListener={(errorMessage: string, newValue: string): void =>
                    updateListenerCallback(form.titleInput, errorMessage, newValue)
                }
                placeholder={loc.strings.request_title_placeholder_text}
                errorMessage={form.titleInput.errorMessage}
                defaultValue={form.titleInput.value}
                maxLength={form.titleInput.maxLength}
                required
                hideErrorMessage={true}
                textFieldType={TextFieldType.Edit}
            />

            {/* Details */}
            <MobileTextField
                hideErrorMessage={true}
                title={loc.strings.additional_details_text}
                updateListener={(errorMessage: string, newValue: string): void =>
                    updateListenerCallback(form.detailsInput, errorMessage, newValue)
                }
                textFieldType={TextFieldType.Details}
                autoAdjustHeight
                resizable={false}
                multiline
                placeholder={loc.strings.additional_details_placeholder_text_mobile}
                defaultValue={form.detailsInput.value}
                maxLength={form.detailsInput.maxLength}
                errorMessage={form.detailsInput.errorMessage}
            />

            {/* Recipient List */}
            <RecipientListInput
                onClick={(): void => setShowRecipientListView(true)}
                recipients={form.recipientsInput.value}
            />

            {/* File Attachment */}
            <FileAttachments
                fileService={fileService}
                showLabel
                accept={fileUploadAcceptedTypes}
                files={form.filesInput.value}
                onFilesUpdate={form.filesInput.setValue}
                required
                approvalType={ApprovalType.ESign.toString()}
                signApprovalType={signApprovalType}
            />
        </>
    );

    const backToForm = (): void => setShowRecipientListView(false);
    const backClick = showRecipientListView ? backToForm : onBack;

    const returnButtonProps: IButtonProps = {
        text: loc.strings.carousel_back,
        onClick: backClick,
    };

    const completeButtonProps: IButtonProps = showRecipientListView
        ? {
              text: loc.strings.done,
              onClick: backClick,
              disabled: !form.recipientsInput.isValid,
          }
        : {
              text: loc.strings.send,
              onClick: (): void => onNext(false),
              disabled: !form.isFormValid,
          };

    const recipientList = (
        <RecipientList
            signApprovalType={signApprovalType}
            availableRoles={form.availableRoles}
            recipientsInput={form.recipientsInput}
            completeInOrderInput={form.completeInOrderInput}
            required
        />
    );

    return (
        <>
            <EsignNavigationBar returnButtonProps={returnButtonProps} completeButtonProps={completeButtonProps} />
            {showRecipientListView ? recipientList : formComponent}
        </>
    );
};
